import { useEffect, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../store/auth";
import { useLocale } from "../hooks/useLocale";
import { isAnonymousRoute } from "../auth/anonymousRoutes";

export function AuthProvider({ children }: { children: ReactNode }) {
  const status = useAuth((s) => s.status);
  const tenant = useAuth((s) => s.tenant);
  const loadTenant = useAuth((s) => s.loadTenant);
  const location = useLocation();
  const { t } = useLocale();

  useEffect(() => {
    if (status === "idle") void loadTenant();
  }, [status, loadTenant]);

  if (status === "idle" || status === "loading") {
    return <div className="full-screen-loader">{t("common.loading")}</div>;
  }

  if (status === "anonymous") {
    if (isAnonymousRoute(location.pathname)) {
      return <>{children}</>;
    }
    return (
      <Navigate to="/login" replace state={{ from: location.pathname }} />
    );
  }

  // Authenticated — keep users away from public-auth pages (they already
  // have a session). The /confirm-email page is an exception: a logged-in
  // user might still legitimately confirm an email change in the future.
  // For now, treat all five anonymous routes as off-limits when logged in.
  if (isAnonymousRoute(location.pathname)) {
    return <Navigate to="/chat" replace />;
  }

  // Authenticated but without any workspace (removed from their last one):
  // the only meaningful destination is the create-workspace screen. Users
  // WITH a workspace may still visit it voluntarily ("+ new account" in the
  // switcher), hence the one-way redirect.
  if (tenant === null && location.pathname !== "/create-workspace") {
    return <Navigate to="/create-workspace" replace />;
  }

  return <>{children}</>;
}
